import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconXClose from "../../../../img/icons/x-close.svg";
import { Link } from "../../../common/Link";
import { IBasket } from "../../../models/catalogue";
import { IWebPageURL, isoWebPageURL } from "../../../models/nominals";
import { getFooterSettings, usePageSetting } from "../../../utils/settings";
import { usePageURL } from "../../../utils/urls";
import { ChatLink } from "../../chat/ChatLink";

import styles from "./ContactUsModalContent.module.scss";

interface ICartIDProps {
    basket: IBasket | null;
}
const CartID = (props: ICartIDProps) => {
    if (!props.basket || props.basket.lines.length <= 0) {
        return null;
    }
    return (
        <div className="service-footer-modal-phone__cart-id">
            {t`Reference Cart ID:`} {props.basket.encoded_basket_id}
        </div>
    );
};

interface ILinksData {
    title: string;
    url: IWebPageURL;
}
interface ILinksProps {
    links: ILinksData[];
}
const Links = (props: ILinksProps) => {
    if (!props.links) {
        return null;
    }
    const renderLinks = props.links.map((link) => {
        return (
            <Link
                key={`${link.url}`}
                className="service-footer-modal-phone__finance-link"
                href={link.url}
                target="_top"
            >
                {link.title}
            </Link>
        );
    });
    return <div className={styles.linkContainer}>{renderLinks}</div>;
};

interface IContentUsModalSectionProps {
    id: string;
    title: string;
    bodyHTML: string;
    phoneLink: IWebPageURL;
    phoneDisplay: string;
    hoursHTML: string;
    children?: React.ReactNode;
}
const ContentUsModalSection = (
    props: IContentUsModalSectionProps,
): React.ReactNode => {
    const [hoursExpanded, setHoursExpanded] = React.useState(false);

    const onToggleHours = () => {
        setHoursExpanded((prev) => !prev);
    };

    const sectionID = `contact-us-modal-section-${props.id}`;
    const hoursTriggerClasses = classNames({
        [styles.hoursTrigger]: true,
        "service-footer-modal-phone__hours-trigger": true,
        [styles.hoursTriggerExpanded]: hoursExpanded,
        "service-footer-modal-phone__hours-trigger--active": hoursExpanded,
    });
    const hoursContainerClasses = classNames({
        [styles.hoursContainer]: true,
        [styles.hoursContainerExpanded]: hoursExpanded,
        "service-footer-modal-phone__hours-container--active": hoursExpanded,
    });
    return (
        <section className={styles.section}>
            <h5>{props.title}</h5>
            <div
                className={`${styles.copy} service-footer-modal-phone__copy`}
                dangerouslySetInnerHTML={{ __html: props.bodyHTML }}
            ></div>
            {props.children}
            <div>
                <Link
                    href={props.phoneLink}
                    className={`${styles.phone} service-footer-modal-phone__phone`}
                    target="_top"
                >
                    {props.phoneDisplay}
                </Link>
                <button
                    className={hoursTriggerClasses}
                    onClick={onToggleHours}
                    aria-controls={sectionID}
                    aria-expanded={hoursExpanded}
                >
                    {t`Hours`}
                </button>
                <div id={sectionID} className={hoursContainerClasses}>
                    <h4>{t`Hours`}</h4>
                    <div
                        className="u-list-unstyled"
                        dangerouslySetInnerHTML={{
                            __html: props.hoursHTML,
                        }}
                    ></div>
                </div>
            </div>
        </section>
    );
};

interface IContactUsModalContentProps {
    basket: IBasket | null;
    onCloseModalContact: (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => void;
    chatOfflineLink?: IWebPageURL;
}
export const ContactUsModalContent = (props: IContactUsModalContentProps) => {
    const preOrderPhoneNumberLink = usePageSetting(
        "pre_order_phone_number_link",
    );
    const preOrderPhoneNumberDisplay = usePageSetting(
        "pre_order_phone_number_display",
    );
    const supportPhoneNumberLink = usePageSetting(
        "support_website_phone_number_link",
    );
    const supportPhoneNumberDisplay = usePageSetting(
        "support_website_phone_number_display",
    );
    const servicePhoneNumberLink = usePageSetting(
        "service_website_phone_number_link",
    );
    const servicePhoneNumberDisplay = usePageSetting(
        "service_website_phone_number_display",
    );
    const contactLink = usePageURL("contact_link");
    const homeLink = usePageURL("home");
    const modalSettings = getFooterSettings();
    if (!modalSettings) {
        // TODO(i18n): Wrap error message in t tagged template: t`Could not find needed footer settings`
        throw new Error("Could not needed find footer settings");
    }
    return (
        <>
            <button
                aria-label={t`close`}
                className={`${styles.close} service-footer-modal__close`}
                onClick={props.onCloseModalContact}
            >
                <SVG
                    aria-hidden={true}
                    className={`${styles.closeIcon} service-footer-modal__close-icon`}
                    src={iconXClose}
                    title={t`Close Icon`}
                />
            </button>
            <div className={styles.content}>
                <div
                    className={`${styles.infoContainer} service-footer-modal__info-container`}
                >
                    <h2 className="service-footer-modal-phone__title">
                        {t`How can we help you?`}
                    </h2>
                    <div>
                        <ContentUsModalSection
                            id="top"
                            title={modalSettings.talk_modal_title_top}
                            bodyHTML={modalSettings.talk_modal_text_top}
                            phoneLink={isoWebPageURL.wrap(
                                preOrderPhoneNumberLink || "/",
                            )}
                            phoneDisplay={preOrderPhoneNumberDisplay || ""}
                            hoursHTML={modalSettings.talk_modal_hours_top}
                        />
                        <ContentUsModalSection
                            id="middle"
                            title={modalSettings.talk_modal_title_middle}
                            bodyHTML={modalSettings.talk_modal_text_middle}
                            phoneLink={isoWebPageURL.wrap(
                                supportPhoneNumberLink || "/",
                            )}
                            phoneDisplay={supportPhoneNumberDisplay || ""}
                            hoursHTML={modalSettings.talk_modal_hours_middle}
                        >
                            <CartID basket={props.basket} />
                        </ContentUsModalSection>
                        <ContentUsModalSection
                            id="bottom"
                            title={modalSettings.talk_modal_title_bottom}
                            bodyHTML={modalSettings.talk_modal_text_bottom}
                            phoneLink={isoWebPageURL.wrap(
                                servicePhoneNumberLink || "/",
                            )}
                            phoneDisplay={servicePhoneNumberDisplay || ""}
                            hoursHTML={modalSettings.talk_modal_hours_bottom}
                        />
                    </div>
                    <div className={styles.chatContainer}>
                        <ChatLink
                            className="button button--inverse service-footer-modal-phone__email-link"
                            chatOfflineLink={
                                props.chatOfflineLink || contactLink || homeLink
                            }
                        />
                    </div>
                    <Links links={modalSettings.talk_modal_links} />
                </div>
            </div>
        </>
    );
};
